import React from 'react';
import styled from 'astroturf/react';

import routes from 'js/core_pre_webpack/routes';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import setTimeoutPromise from 'js/utils/setTimeoutPromise';
import { trackClick } from 'js/utils/trackClick';

import type { ArmSpec } from '../armRegistry';
import { isDownloadRead, isMobileViewport } from '../openerIntent';
import { loswpWorkId } from '../workContext';

const DELIVERY_TIMEOUT_MS = 1500;

const DESK_COPY =
  "Download now, or read it later on your laptop. It'll be saved for you.";

const Note = styled.p`
  margin-top: var(--spacing-xxs-8, 8px);
  font-family: var(--type-font-family-sans-serif, 'DM Sans', sans-serif);
  font-size: 14px;
  line-height: 1.4;
  color: var(--text-primary, #26262b);
  text-align: center;
`;

// 'download' is mandatory: Bookmark::SOURCES is a fixed whitelist that rejects
// arm names, and this source is what saves the work to the library the copy
// promises.
async function postBookmark(): Promise<boolean> {
  const workId = loswpWorkId();
  if (workId === null) return false;
  try {
    const response = await fetchWithCsrf(
      routes.api_v0_bookmarks_path({
        'bookmark[content_id]': workId,
        'bookmark[content_type]': 'Work',
        'bookmark[source]': 'download',
        subdomain_param: 'api',
      }),
      // keepalive: the wrapper's 1500ms race can lose to the delivery, and
      // navigate() then unloads the page — the write must survive it.
      { method: 'POST', keepalive: true },
    );
    return response.ok;
  } catch {
    return false;
  }
}

async function deliverToDesk(): Promise<void> {
  const delivered = await postBookmark();
  trackClick({
    source: 'login_modal_autotest_bookmark_delivery',
    extra: { autotest_bucket: 'send_to_desk', delivered },
  });
}

// The one seam between slots and wrapOnSuccess: the bookmark write is only
// promised when the copy actually rendered this open, so the wrapper is a
// pure passthrough on predicate-fail opens.
let armed = false;

export const sendToDeskSpec: ArmSpec = {
  slots: (ctx) => {
    armed = isMobileViewport() && isDownloadRead(ctx.detail);
    return armed ? { underCta: <Note>{DESK_COPY}</Note> } : undefined;
  },
  stamp: (ctx) => ({
    render_state:
      isMobileViewport() && isDownloadRead(ctx.detail)
        ? 'rendered'
        : 'fallback',
  }),
  // Navigation must never be lost: the delivery races a hard budget and the
  // redirect fires from finally on success, failure, or hang.
  wrapOnSuccess: (navigate) => (redirect) => {
    if (!armed) {
      navigate(redirect);
      return;
    }
    void (async () => {
      try {
        await Promise.race([
          deliverToDesk(),
          setTimeoutPromise(() => undefined, DELIVERY_TIMEOUT_MS),
        ]);
      } finally {
        navigate(redirect);
      }
    })();
  },
};
